/**
 * ───────────────────────────────────────────────────────────
 *  StaticArticle
 * ───────────────────────────────────────────────────────────
 * Enhances the “static-article” section with three features:
 *   1. Converts any `[data-rich-text]` elements into <p>/<h*> markup
 *      via the theme’s RichText helper.
 *   2. Validates and submits the built-in comment form through the
 *      generic Forms helper.
 *   3. Turns a list of <picture> elements into a sideways scrolling
 *      hero carousel that pins itself while the user scrolls.
 *
 * DEPENDENCIES
 *   • GSAP v3 + ScrollTrigger (for the carousel motion).
 *   • window.Sections, RichText, and Forms helpers provided by the
 *     theme’s core JavaScript.
 *
 * EXPECTED MARKUP (simplified)
 * <section data-section-type="static-article">
 *   <div data-articlecomments-form>…</div>         <!-- optional -->
 *
 *   <div data-carousel-container>
 *     <picture>…</picture>
 *     <picture>…</picture>
 *     <picture>…</picture> <!-- 3+ pictures recommended -->
 *   </div>
 * </section>
 *
 * CSS HOOKS INSERTED BY THIS SCRIPT
 *   None.  All styling is left to the theme’s stylesheet.
 *
 */
class StaticArticle {
  /**
   * @param  section
   *   Section object supplied by `window.Sections`.
   */
  constructor(section) {
    this.$el              = window.jQuery(section.el);
    this.$commentForm     = this.$el.find('[data-articlecomments-form]');
    this.richText         = new RichText(this.$el);

    this.carouselContainer= this.$el.find('[data-carousel-container]');
    this.cards            = Array.from(
                              this.carouselContainer[0].querySelectorAll('picture')
                            );

    if (this.$commentForm.length) {
      this.commentForm = new Forms(this.$commentForm);
    }

    // Defer GSAP carousel build until images have actually loaded, so the
    // pinned layout measures real dimensions instead of racing a fixed timer.
    if (this.cards.length > 2) {
      this._whenImagesReady(this.cards).then(() => this._createMotion(this.cards));
    }
  }

  /* ------------------------------------------------------------------
   * Private helpers
   * ---------------------------------------------------------------- */

  /**
   * Resolves once every <img> inside the given <picture> elements has
   * finished loading (or errored). Already-loaded images resolve instantly.
   *
   * @param {HTMLElement[]} pictures – Array of <picture> nodes.
   * @returns {Promise<void>}
   */
  _whenImagesReady(pictures) {
    const images = pictures
      .map(picture => picture.querySelector('img'))
      .filter(Boolean);

    return Promise.all(images.map(img => img.complete
      ? Promise.resolve()
      : new Promise(resolve => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        })));
  }

  /**
   * Creates a horizontal scroll-driven carousel that pins the picture
   * strip while the user scrolls through it.
   *
   * @param {HTMLElement[]} cards – Array of <picture> nodes.
   */
  _createMotion(cards) {
    if (!cards?.length) return;

    const frames = gsap.utils.toArray(cards);

    // Keep a handle on the tween (and its ScrollTrigger) so onSectionUnload
    // can kill just this section's trigger — otherwise theme-editor re-saves
    // accumulate orphaned triggers.
    this.motion = gsap.to(frames, {
      xPercent: -100 * (frames.length - 1),
      ease: 'sine.out',
      scrollTrigger: {
        trigger: this.carouselContainer[0],
        pin: true,
        scrub: 3,
        snap: 1 / (frames.length - 1),
        end: `+=${this.carouselContainer[0].offsetWidth}`
      }
    });
  }

  /* ------------------------------------------------------------------
   * Theme-editor lifecycle hook. The Sections framework calls
   * onSectionUnload (never a plain unload()), so all teardown lives here.
   * ---------------------------------------------------------------- */
  onSectionUnload() {
    this.richText.unload();
    if (this.commentForm) this.commentForm.unload();
    // Kill this section's scroll-driven tween and its ScrollTrigger so
    // repeated editor saves don't leave orphaned triggers behind.
    if (this.motion) {
      this.motion.scrollTrigger?.kill();
      this.motion.kill();
      this.motion = null;
    }
  }
}

/* ───────────────────────────────────────────────────────────
 *  Section registration
 * ─────────────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  const sections = new window.Sections();
  sections.register('static-article', sec => new StaticArticle(sec));
});
